import KnowledgeBaseSettings from './KnowledgeBaseSettings';
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Slider,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Chip,
  Grid,
  IconButton,
  Alert,
  Autocomplete,
  Divider,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useCapabilities } from '../contexts/CapabilitiesContext';
import { calAvatars, getCalAvatar, saveCalAvatarPreference } from '../config/calAvatars';
import { listDatasets, DatasetInfo, fetchMbSettings, saveMbSettings, DEFAULT_MB_MARGIN_PCT } from '../utils/massBalanceStore';
import { setUnitSystem, unitLabels } from '../utils/units';

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT || '';

interface UserPreferencesProps {
  open: boolean;
  onClose: () => void;
  userEmail: string;
  // Called when a save kicked off a fleet recalculation, so the dashboard can show its
  // recalculating cue (spinning refresh button + progress toast) after Settings closes.
  onRefreshTriggered?: () => void;
}

interface ProductPricing {
  product_type: string;
  product_name: string;
  price_per_barrel: number;
  tolerance: number;  // Control limit tolerance (e.g., 0.0025 for ±0.25%)
  unit: string;
  last_updated?: string;
  active: boolean;
}

interface MeterFlowRate {
  meter_product_id: string;
  meter_id: string;
  product_type: string;
  display_name: string;
  average_annual_flow: number;
}

interface Preferences {
  global: {
    auto_corrections_enabled: boolean;
    default_confidence_threshold: number;
    default_auto_apply_threshold: number;
    require_human_review_below: number;
    spike_threshold?: number;  // Default 0.0025 (0.25%)
    bias_threshold?: number;   // Default 3 consecutive readings
    drift_threshold?: number;  // Default 5 consecutive points all on same side of control limits
    enable_overdue_warning?: boolean;  // Overdue provings add a Warning (default off)
    overdue_mode?: 'fixed' | 'average'; // Fixed days, or the meter's average interval
    overdue_threshold_days?: number;   // Days before "overdue" (fixed mode, default 150)
  };
  regression_settings?: {
    enabled?: boolean;             // Master on/off for regression models (default: true)
    minimum_data_points?: number;  // Minimum provings before a model is fit (default: 20)
    minimum_r_squared?: number;    // Minimum R² to display a model (default: 0.5)
    confidence_level?: number;     // Prediction/confidence interval level (default: 0.95)
    drift_affects_health?: boolean; // Admin: drift nudges health to Warning (default: false)
    projection_points?: number;    // Future provings to project on charts (0-5, default: 0)
    recent_predict_points?: number; // Provings 'Predict Recent' predicts (5-30, default: 10)
  };
  repeatability_settings?: {
    enabled?: boolean;   // HM 11 §7.3 repeatability validity gate (default: false)
    limit?: number;      // Percent; HM 11 unheated ±0.05% (default: 0.05)
    action?: 'invalidate' | 'flag'; // Exclude bad prove from stats, or just flag it
  };
  unit_system?: 'imperial' | 'metric'; // Display-only labels: bbl/lb vs m³/MT (default imperial)
  required_fields: string[];
  field_settings: {
    [field: string]: {
      is_required: boolean;
      confidence_threshold: number;
      auto_correct_enabled: boolean;
      auto_apply_threshold: number;
      validation_rules?: any;
    };
  };
  pattern_settings: {
    [pattern: string]: {
      enabled: boolean;
      auto_apply_threshold?: number;
    };
  };
  product_pricing: {
    [product_type: string]: ProductPricing;
  };
  notifications: {
    email_on_low_confidence: boolean;
    daily_summary: boolean;
    auto_correction_alerts: boolean;
  };
}

const AVAILABLE_FIELDS = [
  'test_date', 'product_type', 'flow_rate', 'api_gravity',
  'meter_id', 'previous_meter_factor', 'meter_factor', 'new_meter_factor',
  'change', 'deviation', 'delta', 'temperature', 'pressure',
  'density', 'k_factor', 'meter_size', 'customer', 'location'
];

// This will be loaded from database
interface CorrectionPattern {
  id: string;
  name: string;
  description: string;
  frequency?: number;
  auto_apply?: boolean;
  original_pattern?: string;
  corrected_pattern?: string;
}

const UserPreferences: React.FC<UserPreferencesProps> = ({ open, onClose, userEmail, onRefreshTriggered }) => {
  const [preferences, setPreferences] = useState<Preferences | null>(null);
  const [correctionPatterns, setCorrectionPatterns] = useState<CorrectionPattern[]>([]);
  const [productsFromMeters, setProductsFromMeters] = useState<string[]>([]);
  const [meterFlowRates, setMeterFlowRates] = useState<MeterFlowRate[]>([]);
  const [modifiedFlowRates, setModifiedFlowRates] = useState<Set<string>>(new Set());
  const [flowRateSearchTerm, setFlowRateSearchTerm] = useState<string>('');
  const [selectedProductForFlow, setSelectedProductForFlow] = useState<string>('all');
  const [selectedCalAvatar, setSelectedCalAvatar] = useState<string>(() => {
    return localStorage.getItem('selectedCalAvatar') || 'cal-original';
  });
  const [loading, setLoading] = useState(false);
  // Settings are shared site-wide; edit is gated. RBAC: with enforcement ON, `can()` reads
  // the capability; with it OFF, `can()` returns true so we fall back to the legacy `isAdmin`
  // flag from the API (kept in lockstep with the backend, which itself falls back to
  // is_admin_user while the flag is off).
  const [isAdmin, setIsAdmin] = useState(false);
  const { can, enforced } = useCapabilities();
  // When RBAC is enforced, gate on the capability; otherwise fall back to the legacy
  // admin-only behavior (matches the backend, which also falls back to is_admin_user off).
  const canEditSettings = enforced ? can('edit_settings') : isAdmin;
  const canEditFlowRates = enforced ? can('edit_pricing_flows') : isAdmin;
  const [saveStatus, setSaveStatus] = useState<{ show: boolean; message: string; severity: 'success' | 'error' }>({
    show: false,
    message: '',
    severity: 'success'
  });
  const { getIdTokenClaims } = useAuth0();
  // ---- Mass Balance settings. PER DATASET: DS1 and DS2 are different refineries with different
  // normal ranges, so one silo-wide margin would be wrong for at least one of them.
  const [mbDatasets, setMbDatasets] = useState<DatasetInfo[]>([]);
  const [mbDataset, setMbDataset] = useState<string>('');
  const [mbMargin, setMbMargin] = useState<string>('');
  const [mbBusy, setMbBusy] = useState(false);
  const [mbMsg, setMbMsg] = useState<string>('');
  const [mbIsDefault, setMbIsDefault] = useState(true);

  // Load preferences and patterns on open
  useEffect(() => {
    if (open) {
      const loadAll = async () => {
        await loadPreferences();
        await loadCorrectionPatterns();
        await loadProductsFromMeters();
        await loadMeterFlowRates();
        await loadMbDatasets();
      };
      loadAll();
    }
  }, [open]);

  const loadMbDatasets = async () => {
    try {
      const t = (await getIdTokenClaims())?.__raw;
      const ds = await listDatasets(t);
      setMbDatasets(ds);
      if (ds.length && !mbDataset) await selectMbDataset(ds[0].name);
    } catch { /* Mass Balance may not be enabled for this user — leave the section empty */ }
  };

  const selectMbDataset = async (name: string) => {
    setMbDataset(name); setMbMsg('');
    try {
      const t = (await getIdTokenClaims())?.__raw;
      const cfg = await fetchMbSettings(name, t);
      // Show the default rather than an empty box: the chart is already using it, so a blank field
      // would imply nothing is in force.
      setMbMargin(String(cfg?.marginPct ?? DEFAULT_MB_MARGIN_PCT));
      setMbIsDefault(cfg?.marginPct == null);
    } catch { setMbMargin(String(DEFAULT_MB_MARGIN_PCT)); setMbIsDefault(true); }
  };

  const saveMbMargin = async () => {
    if (!mbDataset) return;
    const raw = mbMargin.trim();
    // Blank clears the override, which puts that dataset back on the default margin.
    const v = raw === '' ? null : Number(raw);
    if (v != null && (!isFinite(v) || v <= 0)) { setMbMsg('Enter a positive percentage, or leave blank to use the default.'); return; }
    setMbBusy(true); setMbMsg('');
    try {
      const t = (await getIdTokenClaims())?.__raw;
      const ok = await saveMbSettings(mbDataset, { marginPct: v }, t);
      if (ok) { setMbIsDefault(v == null); if (v == null) setMbMargin(String(DEFAULT_MB_MARGIN_PCT)); }
      setMbMsg(ok ? (v == null ? `Cleared — ${mbDataset} uses the default ±${DEFAULT_MB_MARGIN_PCT}%.` : `Target saved: ±${v}% for ${mbDataset}.`)
                  : 'Could not save — try again.');
    } finally { setMbBusy(false); }
  };

  const loadCorrectionPatterns = async () => {
    try {
      // Get Auth0 ID token
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const response = await axios.get(`${API_ENDPOINT}/correction-patterns`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      if (response.data.patterns) {
        setCorrectionPatterns(response.data.patterns);
      }
    } catch (error) {
      console.error('Failed to load correction patterns:', error);
      // Use a few default patterns if load fails
      setCorrectionPatterns([
        { id: 'meter_id_ban_to_bay', name: 'BAN → BAY correction', description: 'OCR fix for meter IDs' },
        { id: 'excel_error_removal', name: 'Excel error removal', description: 'Remove #DIV/0! errors' },
        { id: 'meter_factor_scale_correction', name: 'Meter factor scaling', description: 'Fix 9954 → 0.9954' }
      ]);
    }
  };

  const loadProductsFromMeters = async () => {
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      // Fetch product pricing which now includes all products from meters
      const response = await axios.get(`${API_ENDPOINT}/product-pricing`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      // Extract unique product types
      const products = response.data.products || [];
      const productTypes: string[] = products.map((p: any) => p.product_type as string);
      const uniqueProducts = Array.from(new Set(productTypes));
      setProductsFromMeters(uniqueProducts);

      // Update preferences with products from meters (with default 0.00 price)
      setPreferences(prev => {
        if (!prev) return prev;

        const updatedPricing = { ...prev.product_pricing };
        uniqueProducts.forEach(product => {
          if (!updatedPricing[product]) {
            updatedPricing[product] = {
              product_type: product,
              product_name: product,
              price_per_barrel: 0.00,
              tolerance: 0.0025,  // Default 0.25%
              unit: 'USD/bbl',
              active: true,
              last_updated: ''
            };
          }
        });

        return {
          ...prev,
          product_pricing: updatedPricing
        };
      });
    } catch (error) {
      console.error('Failed to load products from meters:', error);
    }
  };

  const loadMeterFlowRates = async () => {
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const response = await axios.get(`${API_ENDPOINT}/meter-flow-rates`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      setMeterFlowRates(response.data.meter_flow_rates || []);
      setModifiedFlowRates(new Set()); // Reset modified tracking
    } catch (error) {
      console.error('Failed to load meter flow rates:', error);
    }
  };

  const saveMeterFlowRates = async () => {
    if (modifiedFlowRates.size === 0) return; // Nothing to save

    setLoading(true);
    try {
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      // Only send modified flow rates
      const updates = meterFlowRates
        .filter(meter => modifiedFlowRates.has(meter.meter_product_id))
        .map(meter => ({
          meter_product_id: meter.meter_product_id,
          average_annual_flow: meter.average_annual_flow
        }));

      await axios.put(`${API_ENDPOINT}/meter-flow-rates`, {
        updates
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      setModifiedFlowRates(new Set()); // Clear modified tracking after successful save
      setSaveStatus({
        show: true,
        message: `Updated ${updates.length} meter flow rates successfully`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving meter flow rates:', error);
      setSaveStatus({
        show: true,
        message: 'Failed to save meter flow rates',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateMeterFlowRate = (meter_product_id: string, flow_rate: number) => {
    setMeterFlowRates(prev => prev.map(meter =>
      meter.meter_product_id === meter_product_id
        ? { ...meter, average_annual_flow: flow_rate }
        : meter
    ));
    setModifiedFlowRates(prev => {
      const newSet = new Set(prev);
      newSet.add(meter_product_id);
      return newSet;
    });
  };

  const loadPreferences = async () => {
    setLoading(true);
    try {
      // Get Auth0 ID token
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const response = await axios.get(`${API_ENDPOINT}/preferences`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      // Merge loaded preferences with defaults to ensure all fields exist
      const defaults = getDefaultPreferences();
      const loaded = response.data.preferences || {};

      const merged: Preferences = {
        ...defaults,
        ...loaded,
        global: {
          ...defaults.global,
          ...(loaded.global || {})
        },
        notifications: {
          ...defaults.notifications,
          ...(loaded.notifications || {})
        },
        field_settings: loaded.field_settings || defaults.field_settings,
        pattern_settings: loaded.pattern_settings || defaults.pattern_settings,
        product_pricing: loaded.product_pricing || {},  // Start with empty object if none saved
        required_fields: loaded.required_fields || defaults.required_fields,
        unit_system: loaded.unit_system || defaults.unit_system || 'imperial'
      };

      // Mirror the display-only unit system to localStorage so label helpers
      // (e.g. the Mass Balance Utility) can read it synchronously without props.
      setUnitSystem(merged.unit_system === 'metric' ? 'metric' : 'imperial');
      setPreferences(merged);
      setIsAdmin(response.data.is_admin === true);
    } catch (error) {
      console.error('Failed to load preferences:', error);
      // Use defaults if load fails
      setPreferences(getDefaultPreferences());
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    if (!preferences) return;
    if (!canEditSettings) {
      setSaveStatus({
        show: true,
        message: enforced ? 'You do not have permission to edit site settings.' : 'Only an admin can edit site settings.',
        severity: 'error'
      });
      return;
    }

    setLoading(true);
    try {
      // Get Auth0 ID token
      const tokenClaims = await getIdTokenClaims();
      const idToken = tokenClaims?.__raw;

      const resp = await axios.put(`${API_ENDPOINT}/preferences`, {
        preferences: preferences
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });

      // When an output-affecting setting changed, the backend kicks off a fleet
      // recalculation so the new setting takes effect. Tell the user it's running.
      const recalculating = resp?.data?.refresh_triggered === true;
      // Signal the dashboard so it shows the recalculating cue once Settings closes.
      if (recalculating) onRefreshTriggered?.();
      setSaveStatus({
        show: true,
        message: recalculating
          ? 'Saved — recalculating all meters with the new settings (this can take ~a minute)…'
          : 'Preferences saved successfully!',
        severity: 'success'
      });

      setTimeout(() => onClose(), recalculating ? 2600 : 1500);
    } catch (error) {
      console.error('Failed to save preferences:', error);
      setSaveStatus({
        show: true,
        message: 'Failed to save preferences',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const getDefaultPreferences = (): Preferences => ({
    global: {
      auto_corrections_enabled: true,
      default_confidence_threshold: 0.7,
      default_auto_apply_threshold: 10,
      require_human_review_below: 0.5,
      spike_threshold: 0.0025,  // Default 0.25%
      bias_threshold: 3,        // Default 3 consecutive readings
      drift_threshold: 5,       // Default 5 consecutive points all on same side
      enable_overdue_warning: false,  // Overdue provings don't warn by default
      overdue_mode: 'fixed',
      overdue_threshold_days: 150
    },
    regression_settings: {
      enabled: true,               // Regression models on by default
      minimum_data_points: 20,     // Default 20 provings minimum
      minimum_r_squared: 0.5,      // Default 0.5 R² minimum to display
      confidence_level: 0.95,      // Default 95% intervals
      drift_affects_health: false, // Drift is a separate indicator by default
      projection_points: 0,        // No future-proving projection by default
      recent_predict_points: 10    // Predict Recent holds out the last 10 by default
    },
    repeatability_settings: {
      enabled: false,       // HM 11 repeatability gate off by default
      limit: 0.05,          // HM 11 unheated ±0.05%
      action: 'invalidate'  // Exclude non-conformant proves from stats
    },
    unit_system: 'imperial',  // Display-only labels: Barrels/Pounds vs m³/MT
    required_fields: ['test_date', 'product_type', 'meter_id', 'meter_factor'],
    field_settings: {},
    pattern_settings: {},
    product_pricing: {},
    notifications: {
      email_on_low_confidence: false,
      daily_summary: false,
      auto_correction_alerts: true
    }
  });

  const updateGlobalSetting = (key: string, value: any) => {
    if (!preferences) return;
    setPreferences({
      ...preferences,
      global: { ...preferences.global, [key]: value }
    });
  };

  const updateFieldSetting = (field: string, setting: string, value: any) => {
    if (!preferences) return;
    setPreferences({
      ...preferences,
      field_settings: {
        ...preferences.field_settings,
        [field]: {
          ...(preferences.field_settings[field] || {}),
          [setting]: value
        }
      }
    });
  };

  const updatePatternSetting = (pattern: string, setting: string, value: any) => {
    if (!preferences) return;
    setPreferences({
      ...preferences,
      pattern_settings: {
        ...preferences.pattern_settings,
        [pattern]: {
          ...(preferences.pattern_settings[pattern] || {}),
          [setting]: value
        }
      }
    });
  };

  const updateProductPricing = (productType: string, pricing: Partial<ProductPricing>) => {
    if (!preferences) return;
    const currentPricing = preferences.product_pricing[productType] || {
      product_type: productType,
      product_name: productType,
      price_per_barrel: 0,
      unit: 'USD/bbl',
      active: true
    };

    setPreferences({
      ...preferences,
      product_pricing: {
        ...preferences.product_pricing,
        [productType]: {
          ...currentPricing,
          ...pricing,
          last_updated: new Date().toISOString()
        }
      }
    });
  };

  const removeProductPricing = (productType: string) => {
    if (!preferences) return;
    const newPricing = { ...preferences.product_pricing };
    delete newPricing[productType];
    setPreferences({
      ...preferences,
      product_pricing: newPricing
    });
  };


  const toggleRequiredField = (field: string) => {
    if (!preferences) return;
    const isRequired = preferences.required_fields.includes(field);

    if (isRequired) {
      setPreferences({
        ...preferences,
        required_fields: preferences.required_fields.filter(f => f !== field)
      });
    } else {
      setPreferences({
        ...preferences,
        required_fields: [...preferences.required_fields, field]
      });
    }

    // Also update field_settings
    updateFieldSetting(field, 'is_required', !isRequired);
  };

  if (!preferences) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <SettingsIcon sx={{ mr: 1 }} />
          User Preferences
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {saveStatus.show && (
          <Alert severity={saveStatus.severity} sx={{ mb: 2 }}>
            {saveStatus.message}
          </Alert>
        )}

        {/* Units (display labels only) */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Units</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Choose the units your site reports in. This changes the <strong>labels only</strong>
                (Barrels/Pounds ↔ m³/MT) across the app — including the Mass Balance Utility — it does
                not convert any values. Your data is already stored in the unit you select.
              </Alert>
              <ToggleButtonGroup
                exclusive
                color="primary"
                value={preferences?.unit_system || 'imperial'}
                onChange={(_e, val) => {
                  if (!val || !preferences) return;
                  setUnitSystem(val);  // immediate localStorage mirror for label helpers
                  setPreferences({ ...preferences, unit_system: val });
                }}
              >
                <ToggleButton value="imperial">Imperial — Barrels / Pounds</ToggleButton>
                <ToggleButton value="metric">Metric — m³ / MT</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Cal Avatar Selection */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Cal Avatar Selection</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Choose your preferred Cal avatar that will appear throughout the application.
              </Alert>

              <Grid container spacing={2}>
                {calAvatars.map((avatar) => (
                  <Grid item xs={12} sm={6} md={3} key={avatar.id}>
                    <Box
                      sx={{
                        border: selectedCalAvatar === avatar.id ? '3px solid' : '1px solid',
                        borderColor: selectedCalAvatar === avatar.id ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        p: 2,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover',
                          transform: 'scale(1.05)'
                        }
                      }}
                      onClick={() => {
                        setSelectedCalAvatar(avatar.id);
                        saveCalAvatarPreference(avatar.id);
                        setSaveStatus({
                          show: true,
                          message: 'Cal avatar updated successfully!',
                          severity: 'success'
                        });
                      }}
                    >
                      <Avatar
                        src={avatar.path}
                        sx={{ width: 80, height: 80 }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Mass Balance — per-dataset loss target. Called "Target" on screen since 15 Oct (Leslie);
            the stored key is still `marginPct`, so values saved under the old name carry over. */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Mass Balance</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {mbDatasets.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No Mass Balance datasets found for this site.
              </Typography>
            ) : (
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Mass Balance Target</strong> — the loss a month may reach before it is flagged.
                    A bar on the Mass Gain/Loss chart turns red when it exceeds this, the headline reads
                    “Within target” or “Outside target”, and the Trends tab counts periods outside it. Set per
                    dataset, since each site has its own normal range. Defaults to{' '}
                    <strong>±{DEFAULT_MB_MARGIN_PCT}%</strong>; leave blank to go back to it.
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={5}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Dataset</InputLabel>
                    <Select label="Dataset" value={mbDataset} onChange={(e) => selectMbDataset(String(e.target.value))}>
                      {mbDatasets.map((d) => <MenuItem key={d.name} value={d.name}>{d.name}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField fullWidth size="small" label="Target (± % of crude)" value={mbMargin}
                    onChange={(e) => { setMbMargin(e.target.value); setMbIsDefault(false); }}
                    helperText={mbIsDefault ? 'Using the default' : 'Set for this dataset'}
                    placeholder={String(DEFAULT_MB_MARGIN_PCT)} inputProps={{ inputMode: 'decimal' }} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Button variant="contained" fullWidth disabled={mbBusy || !mbDataset} onClick={saveMbMargin}>
                    {mbBusy ? 'Saving…' : 'Save'}
                  </Button>
                </Grid>
                {mbMsg && (
                  <Grid item xs={12}>
                    <Alert severity={mbMsg.startsWith('Could not') || mbMsg.startsWith('Enter') ? 'warning' : 'success'}>{mbMsg}</Alert>
                  </Grid>
                )}
              </Grid>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Global Settings */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Global Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.global.auto_corrections_enabled}
                      onChange={(e) => updateGlobalSetting('auto_corrections_enabled', e.target.checked)}
                    />
                  }
                  label="Enable Auto-Corrections"
                />
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>
                  Default Confidence Threshold: {(preferences.global.default_confidence_threshold * 100).toFixed(0)}%
                </Typography>
                <Slider
                  value={preferences.global.default_confidence_threshold}
                  onChange={(_, value) => updateGlobalSetting('default_confidence_threshold', value)}
                  min={0}
                  max={1}
                  step={0.05}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 0.5, label: '50%' },
                    { value: 1, label: '100%' }
                  ]}
                />
                <Typography variant="caption" color="textSecondary">
                  Fields below this confidence will trigger human review
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>
                  Auto-Apply After: {preferences.global.default_auto_apply_threshold} corrections
                </Typography>
                <Slider
                  value={preferences.global.default_auto_apply_threshold}
                  onChange={(_, value) => updateGlobalSetting('default_auto_apply_threshold', value)}
                  min={1}
                  max={50}
                  step={1}
                  marks={[
                    { value: 1, label: '1' },
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' }
                  ]}
                />
                <Typography variant="caption" color="textSecondary">
                  Number of user corrections before pattern auto-applies
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
                  Anomaly Detection Settings
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>
                  Spike Threshold: {((preferences.global.spike_threshold || 0.0025) * 100).toFixed(2)}%
                </Typography>
                <Slider
                  value={(preferences.global.spike_threshold || 0.0025) * 100}
                  onChange={(_, value) => updateGlobalSetting('spike_threshold', (value as number) / 100)}
                  min={0.1}
                  max={1.0}
                  step={0.05}
                  marks={[
                    { value: 0.1, label: '0.10%' },
                    { value: 0.25, label: '0.25%' },
                    { value: 0.5, label: '0.50%' },
                    { value: 1.0, label: '1.00%' }
                  ]}
                />
                <Typography variant="caption" color="textSecondary">
                  Meter factor change threshold to trigger spike detection (e.g., 0.25% = ±0.0025 change)
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>
                  Bias Threshold: {preferences.global.bias_threshold || 3} consecutive readings
                </Typography>
                <Slider
                  value={preferences.global.bias_threshold || 3}
                  onChange={(_, value) => updateGlobalSetting('bias_threshold', value)}
                  min={2}
                  max={10}
                  step={1}
                  marks={[
                    { value: 2, label: '2' },
                    { value: 3, label: '3' },
                    { value: 5, label: '5' },
                    { value: 10, label: '10' }
                  ]}
                />
                <Typography variant="caption" color="textSecondary">
                  Number of consecutive readings within spike range to detect bias (sustained shift)
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Typography gutterBottom>
                  Drift Threshold: {preferences.global.drift_threshold || 5} consecutive points
                </Typography>
                <Slider
                  value={preferences.global.drift_threshold || 5}
                  onChange={(_, value) => updateGlobalSetting('drift_threshold', value)}
                  min={3}
                  max={10}
                  step={1}
                  marks={[
                    { value: 3, label: '3' },
                    { value: 5, label: '5' },
                    { value: 7, label: '7' },
                    { value: 10, label: '10' }
                  ]}
                />
                <Typography variant="caption" color="textSecondary">
                  Number of consecutive points ALL above UCL or ALL below LCL to detect drift
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={preferences.global.enable_overdue_warning === true}
                      onChange={(e) => updateGlobalSetting('enable_overdue_warning', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Flag meters overdue for proving as a Warning"
                />
                <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                  When on, a meter that hasn't been proved in time is marked Warning (yellow). It never
                  overrides an out-of-control (red) meter. When off, health is based purely on the
                  control chart.
                </Typography>
                {preferences.global.enable_overdue_warning && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" gutterBottom>How is "overdue" determined?</Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={preferences.global.overdue_mode || 'fixed'}
                      onChange={(_, val) => { if (val) updateGlobalSetting('overdue_mode', val); }}
                      sx={{ mb: 1 }}
                    >
                      <ToggleButton value="fixed">Fixed number of days</ToggleButton>
                      <ToggleButton value="average">Average interval between provings</ToggleButton>
                    </ToggleButtonGroup>
                    {(preferences.global.overdue_mode || 'fixed') === 'average' ? (
                      <Typography variant="caption" color="textSecondary" display="block">
                        Each meter is flagged once it exceeds its own average time between provings.
                      </Typography>
                    ) : (
                      <>
                        <Typography gutterBottom>
                          Overdue After: {preferences.global.overdue_threshold_days || 150} days
                        </Typography>
                        <Slider
                          value={preferences.global.overdue_threshold_days || 150}
                          onChange={(_, value) => updateGlobalSetting('overdue_threshold_days', value)}
                          min={30}
                          max={365}
                          step={15}
                          marks={[
                            { value: 30, label: '30' },
                            { value: 150, label: '150' },
                            { value: 365, label: '365' }
                          ]}
                          valueLabelDisplay="auto"
                        />
                      </>
                    )}
                  </Box>
                )}
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Required Fields */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">
              Required Fields ({preferences.required_fields.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Autocomplete
              multiple
              options={AVAILABLE_FIELDS}
              value={preferences.required_fields}
              onChange={(_, newValue) => setPreferences({ ...preferences, required_fields: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  label="Select Required Fields"
                  placeholder="Choose fields"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    {...getTagProps({ index })}
                    color="primary"
                  />
                ))
              }
            />
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
              Documents missing these fields will be flagged for review
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Repeatability Validity Gate (HM 11 §7.3) */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Repeatability Validity (HM 11)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                HM 11 §7.3 proving-validity gate. When enabled, a proving whose
                repeatability exceeds the limit is treated as non-conformant. Changing
                any setting here re-runs the aggregator so it takes effect site-wide.
              </Alert>
              <FormControlLabel
                sx={{ mb: 2, display: 'block' }}
                control={
                  <Switch
                    checked={preferences.repeatability_settings?.enabled === true}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      repeatability_settings: {
                        ...preferences.repeatability_settings,
                        enabled: e.target.checked
                      }
                    })}
                    color="primary"
                  />
                }
                label="Flag proves that fail the repeatability tolerance"
              />
              {preferences.repeatability_settings?.enabled && (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" gutterBottom>
                      Repeatability limit: {(preferences.repeatability_settings?.limit ?? 0.05).toFixed(2)}%
                    </Typography>
                    <Slider
                      value={preferences.repeatability_settings?.limit ?? 0.05}
                      onChange={(_, value) => setPreferences({
                        ...preferences,
                        repeatability_settings: {
                          ...preferences.repeatability_settings,
                          limit: value as number
                        }
                      })}
                      min={0.01}
                      max={0.20}
                      step={0.01}
                      marks={[
                        { value: 0.05, label: '0.05% (unheated)' },
                        { value: 0.10, label: '0.10% (heated)' }
                      ]}
                      valueLabelDisplay="auto"
                    />
                    <Typography variant="caption" color="text.secondary">
                      HM 11: repeatability = (max run − min run) ÷ mean, over ≥3 runs.
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" gutterBottom>
                      When a prove fails
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={preferences.repeatability_settings?.action ?? 'invalidate'}
                      onChange={(_, value) => value && setPreferences({
                        ...preferences,
                        repeatability_settings: {
                          ...preferences.repeatability_settings,
                          action: value as 'invalidate' | 'flag'
                        }
                      })}
                    >
                      <ToggleButton value="invalidate">Invalidate + flag</ToggleButton>
                      <ToggleButton value="flag">Flag only</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      Invalidate excludes the bad prove from control-limit stats (HM 11 intent); Flag keeps it in the math but marks it non-conformant.
                    </Typography>
                  </Grid>
                </Grid>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Regression Analysis Settings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Regression Analysis Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Configure how meter factor regression models are calculated for the
                Meter Factor Control Chart overlay. Every proving is used — records
                missing a predictor are weighted down, never discarded.
              </Alert>
              <FormControlLabel
                sx={{ mb: 2 }}
                control={
                  <Switch
                    checked={preferences.regression_settings?.enabled !== false}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      regression_settings: {
                        ...preferences.regression_settings,
                        enabled: e.target.checked
                      }
                    })}
                    color="primary"
                  />
                }
                label="Enable regression models"
              />
              <FormControlLabel
                sx={{ mb: 2, display: 'block' }}
                control={
                  <Switch
                    checked={preferences.regression_settings?.drift_affects_health === true}
                    onChange={(e) => setPreferences({
                      ...preferences,
                      regression_settings: {
                        ...preferences.regression_settings,
                        drift_affects_health: e.target.checked
                      }
                    })}
                    color="primary"
                  />
                }
                label="Let regression drift affect health (nudges a healthy meter to Warning when it drifts from its forecast; never overrides a control-limit Critical)"
              />
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" gutterBottom>
                    Minimum Data Points: {preferences.regression_settings?.minimum_data_points || 20}
                  </Typography>
                  <Slider
                    value={preferences.regression_settings?.minimum_data_points || 20}
                    onChange={(_, value) => setPreferences({
                      ...preferences,
                      regression_settings: {
                        ...preferences.regression_settings,
                        minimum_data_points: value as number
                      }
                    })}
                    min={5}
                    max={50}
                    step={5}
                    marks={[
                      { value: 5, label: '5' },
                      { value: 20, label: '20' },
                      { value: 50, label: '50' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Minimum number of proving reports needed to calculate regression
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" gutterBottom>
                    Minimum R² Value: {((preferences.regression_settings?.minimum_r_squared || 0.5) * 100).toFixed(0)}%
                  </Typography>
                  <Slider
                    value={(preferences.regression_settings?.minimum_r_squared || 0.5) * 100}
                    onChange={(_, value) => setPreferences({
                      ...preferences,
                      regression_settings: {
                        ...preferences.regression_settings,
                        minimum_r_squared: (value as number) / 100
                      }
                    })}
                    min={0}
                    max={100}
                    step={10}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 50, label: '50%' },
                      { value: 100, label: '100%' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Minimum R² value for regression model to be considered valid
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" gutterBottom>
                    Confidence Level: {((preferences.regression_settings?.confidence_level || 0.95) * 100).toFixed(0)}%
                  </Typography>
                  <Slider
                    value={(preferences.regression_settings?.confidence_level || 0.95) * 100}
                    onChange={(_, value) => setPreferences({
                      ...preferences,
                      regression_settings: {
                        ...preferences.regression_settings,
                        confidence_level: (value as number) / 100
                      }
                    })}
                    min={80}
                    max={99}
                    step={null}
                    marks={[
                      { value: 80, label: '80%' },
                      { value: 90, label: '90%' },
                      { value: 95, label: '95%' },
                      { value: 98, label: '98%' },
                      { value: 99, label: '99%' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Confidence level for prediction and confidence intervals
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" gutterBottom>
                    Project Future Provings: {preferences.regression_settings?.projection_points || 0}
                  </Typography>
                  <Slider
                    value={preferences.regression_settings?.projection_points || 0}
                    onChange={(_, value) => setPreferences({
                      ...preferences,
                      regression_settings: {
                        ...preferences.regression_settings,
                        projection_points: value as number
                      }
                    })}
                    min={0}
                    max={5}
                    step={1}
                    marks={[
                      { value: 0, label: 'Off' },
                      { value: 5, label: '5' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Draw up to 5 projected future provings (damped-trend forecast of the
                    out-of-sample path). Shown in Ensemble mode on the control charts and
                    Regression Utility.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Typography variant="body2" gutterBottom>
                    Predict Recent — Provings: {preferences.regression_settings?.recent_predict_points || 10}
                  </Typography>
                  <Slider
                    value={preferences.regression_settings?.recent_predict_points || 10}
                    onChange={(_, value) => setPreferences({
                      ...preferences,
                      regression_settings: {
                        ...preferences.regression_settings,
                        recent_predict_points: value as number
                      }
                    })}
                    min={5}
                    max={30}
                    step={1}
                    marks={[
                      { value: 5, label: '5' },
                      { value: 10, label: '10' },
                      { value: 30, label: '30' }
                    ]}
                    valueLabelDisplay="auto"
                  />
                  <Typography variant="caption" color="text.secondary">
                    How many of the most recent provings "Predict Recent" mode holds out
                    and predicts (trains on everything before them).
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Field-Specific Settings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Field-Specific Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              {preferences.required_fields.map(field => {
                const settings = preferences.field_settings[field] || {};
                return (
                  <ListItem key={field} divider>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" fontWeight="bold">
                          {field}
                        </Typography>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" gutterBottom>
                                Confidence Threshold: {((settings.confidence_threshold || preferences.global.default_confidence_threshold) * 100).toFixed(0)}%
                              </Typography>
                              <Slider
                                size="small"
                                value={settings.confidence_threshold || preferences.global.default_confidence_threshold}
                                onChange={(_, value) => updateFieldSetting(field, 'confidence_threshold', value)}
                                min={0}
                                max={1}
                                step={0.05}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" gutterBottom>
                                Auto-Apply After: {settings.auto_apply_threshold || preferences.global.default_auto_apply_threshold} corrections
                              </Typography>
                              <Slider
                                size="small"
                                value={settings.auto_apply_threshold || preferences.global.default_auto_apply_threshold}
                                onChange={(_, value) => updateFieldSetting(field, 'auto_apply_threshold', value)}
                                min={1}
                                max={50}
                                step={1}
                              />
                            </Grid>

                            <Grid item xs={12}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    size="small"
                                    checked={settings.auto_correct_enabled !== false}
                                    onChange={(e) => updateFieldSetting(field, 'auto_correct_enabled', e.target.checked)}
                                  />
                                }
                                label="Enable auto-corrections for this field"
                              />
                            </Grid>
                          </Grid>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Pattern Settings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">Correction Patterns</Typography>
              <Chip
                label={`${correctionPatterns.length} patterns loaded`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <List>
              {correctionPatterns.map(pattern => {
                const settings = preferences.pattern_settings[pattern.id] || {};
                return (
                  <ListItem key={pattern.id} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">{pattern.name}</Typography>
                          {pattern.frequency && pattern.frequency > 0 && (
                            <Chip
                              label={`Used ${pattern.frequency} times`}
                              size="small"
                              color="info"
                              variant="outlined"
                            />
                          )}
                          {pattern.auto_apply && (
                            <Chip
                              label="Auto-Apply"
                              size="small"
                              color="success"
                              variant="filled"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption">{pattern.description}</Typography>
                          {pattern.original_pattern && pattern.corrected_pattern && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                              Corrects: "{pattern.original_pattern}" → "{pattern.corrected_pattern}"
                            </Typography>
                          )}
                          {settings.auto_apply_threshold && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" gutterBottom>
                                Custom threshold: {settings.auto_apply_threshold} corrections
                              </Typography>
                              <Slider
                                size="small"
                                value={settings.auto_apply_threshold}
                                onChange={(_, value) => updatePatternSetting(pattern.id, 'auto_apply_threshold', value)}
                                min={1}
                                max={50}
                                step={1}
                              />
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <Switch
                        checked={settings.enabled !== false}
                        onChange={(e) => updatePatternSetting(pattern.id, 'enabled', e.target.checked)}
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* Product Pricing */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">Product Pricing</Typography>
              <Chip
                label={`${Object.keys(preferences?.product_pricing || {}).length} products configured`}
                size="small"
                color="primary"
                variant="outlined"
              />
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Configure product prices and control limit tolerances for cost of non-compliance calculations.
                Cal will use these prices to calculate financial impact when meters drift.
                Tolerance defines the control limit width (e.g., 0.25% = ±0.0025 from baseline).
              </Alert>

              {/* Add new product button */}
              <Box sx={{ mb: 2 }}>
                <Button
                  startIcon={<AddIcon />}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    const newProductType = prompt('Enter product type (e.g., REG, PREM, ETH):');
                    if (newProductType) {
                      updateProductPricing(newProductType.toUpperCase(), {
                        product_type: newProductType.toUpperCase(),
                        product_name: newProductType.toUpperCase(),
                        price_per_barrel: 0,
                        tolerance: 0.0025,  // Default 0.25%
                        unit: 'USD/bbl',
                        active: true
                      });
                    }
                  }}
                >
                  Add Product
                </Button>
              </Box>

              {/* Product list */}
              <List>
                {Object.values(preferences?.product_pricing || {}).map((product) => (
                  <ListItem key={product.product_type} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">{product.product_name || product.product_type}</Typography>
                          {product.active && (
                            <Chip label="Active" size="small" color="success" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={3}>
                              <TextField
                                label="Product Name"
                                value={product.product_name}
                                onChange={(e) => updateProductPricing(product.product_type, { product_name: e.target.value })}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <TextField
                                label={`Price (USD${unitLabels().perVol})`}
                                type="number"
                                value={product.price_per_barrel}
                                onChange={(e) => updateProductPricing(product.product_type, { price_per_barrel: parseFloat(e.target.value) || 0 })}
                                size="small"
                                fullWidth
                                InputProps={{
                                  startAdornment: <Typography variant="caption" sx={{ mr: 1 }}>$</Typography>
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <Tooltip title="Control limit tolerance percentage (e.g., 0.25 = ±0.25%)">
                                <TextField
                                  label="Tolerance %"
                                  type="number"
                                  value={(product.tolerance || 0.0025) * 100}  // Convert to percentage
                                  onChange={(e) => {
                                    const percentage = parseFloat(e.target.value) || 0.25;
                                    updateProductPricing(product.product_type, {
                                      tolerance: percentage / 100  // Convert back to decimal
                                    });
                                  }}
                                  size="small"
                                  fullWidth
                                  inputProps={{
                                    min: 0.1,
                                    max: 1.0,
                                    step: 0.05
                                  }}
                                  InputProps={{
                                    endAdornment: <Typography variant="caption" sx={{ ml: 1 }}>%</Typography>
                                  }}
                                />
                              </Tooltip>
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <TextField
                                label="Unit"
                                value={product.unit}
                                onChange={(e) => updateProductPricing(product.product_type, { unit: e.target.value })}
                                size="small"
                                fullWidth
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={product.active}
                                    onChange={(e) => updateProductPricing(product.product_type, { active: e.target.checked })}
                                  />
                                }
                                label="Active"
                              />
                            </Grid>
                            <Grid item xs={12} md={2}>
                              <IconButton
                                onClick={() => removeProductPricing(product.product_type)}
                                color="error"
                                size="small"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Grid>
                          </Grid>
                          {product.last_updated && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Last updated: {new Date(product.last_updated).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
                {Object.keys(preferences?.product_pricing || {}).length === 0 && (
                  <ListItem>
                    <Typography variant="body2" color="text.secondary">
                      No products configured yet. Add products to enable cost calculations.
                    </Typography>
                  </ListItem>
                )}
              </List>
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Meter/Product Annual Flow Rates */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6">Meter/Product Annual Flow Rates</Typography>
              <Chip
                label={`${meterFlowRates.length} meter-products`}
                size="small"
                color="primary"
                variant="outlined"
              />
              {modifiedFlowRates.size > 0 && (
                <Chip
                  label={`${modifiedFlowRates.size} modified`}
                  size="small"
                  color="warning"
                  variant="filled"
                />
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box>
              <Alert severity="info" sx={{ mb: 2 }}>
                Configure annual flow rates ({unitLabels().volYear}) for each meter-product combination. These values are used to calculate financial impact in the Cost of Non-Compliance analysis.
              </Alert>

              {/* Search and Filter Controls */}
              <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <TextField
                  placeholder="Search meters..."
                  size="small"
                  sx={{ width: 250 }}
                  value={flowRateSearchTerm}
                  onChange={(e) => setFlowRateSearchTerm(e.target.value)}
                />

                {/* Product filter dropdown */}
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Product Type</InputLabel>
                  <Select
                    value={selectedProductForFlow}
                    onChange={(e) => setSelectedProductForFlow(e.target.value)}
                    label="Product Type"
                  >
                    <MenuItem value="all">All Products</MenuItem>
                    {Array.from(new Set(meterFlowRates.map(m => m.product_type)))
                      .sort()
                      .map(product => (
                        <MenuItem key={product} value={product}>{product}</MenuItem>
                      ))}
                  </Select>
                </FormControl>

                {/* Bulk update button with product filter */}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => {
                    const targetProduct = selectedProductForFlow === 'all' ? 'ALL meters' : `all ${selectedProductForFlow} meters`;
                    const newFlow = prompt(`Enter flow rate to apply to ${targetProduct} (${unitLabels().volYear}):`);
                    if (newFlow && !isNaN(parseFloat(newFlow))) {
                      const flowValue = parseFloat(newFlow);
                      const updatedIds: string[] = [];

                      setMeterFlowRates(prev => prev.map(meter => {
                        if (selectedProductForFlow === 'all' || meter.product_type === selectedProductForFlow) {
                          updatedIds.push(meter.meter_product_id);
                          return { ...meter, average_annual_flow: flowValue };
                        }
                        return meter;
                      }));

                      // Mark all updated meters as modified
                      setModifiedFlowRates(prev => {
                        const newSet = new Set(prev);
                        updatedIds.forEach(id => newSet.add(id));
                        return newSet;
                      });
                    }
                  }}
                >
                  Set {selectedProductForFlow === 'all' ? 'All' : selectedProductForFlow} Flow Rates
                </Button>

                {/* Clear search button */}
                {flowRateSearchTerm && (
                  <Button
                    size="small"
                    onClick={() => setFlowRateSearchTerm('')}
                  >
                    Clear Search
                  </Button>
                )}
              </Box>

              {/* Meter flow rates list */}
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {(() => {
                  const filteredMeters = meterFlowRates.filter(meter => {
                    // Apply search filter
                    const searchLower = flowRateSearchTerm.toLowerCase();
                    const matchesSearch = !searchLower ||
                      meter.meter_product_id.toLowerCase().includes(searchLower) ||
                      (meter.display_name && meter.display_name.toLowerCase().includes(searchLower));

                    // Apply product filter
                    const matchesProduct = selectedProductForFlow === 'all' ||
                      meter.product_type === selectedProductForFlow;

                    return matchesSearch && matchesProduct;
                  });

                  // Show count if filtered
                  if (flowRateSearchTerm || selectedProductForFlow !== 'all') {
                    if (filteredMeters.length === 0) {
                      return (
                        <ListItem>
                          <Typography variant="body2" color="text.secondary">
                            No meters match your search criteria
                          </Typography>
                        </ListItem>
                      );
                    }
                    // Add a header showing filtered count
                    return (
                      <>
                        <ListItem>
                          <Typography variant="body2" color="text.secondary">
                            Showing {filteredMeters.length} of {meterFlowRates.length} meters
                          </Typography>
                        </ListItem>
                        {filteredMeters.map((meter) => (
                  <ListItem key={meter.meter_product_id} divider>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">
                            {meter.display_name || meter.meter_product_id}
                          </Typography>
                          <Chip label={meter.product_type} size="small" />
                          {modifiedFlowRates.has(meter.meter_product_id) && (
                            <Chip label="Modified" size="small" color="warning" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 1 }}>
                          <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={4}>
                              <TextField
                                label={`Annual Flow (${unitLabels().volYear})`}
                                type="number"
                                value={meter.average_annual_flow}
                                onChange={(e) => updateMeterFlowRate(meter.meter_product_id, parseFloat(e.target.value) || 0)}
                                size="small"
                                fullWidth
                                InputProps={{
                                  inputProps: { min: 0, step: 1000 }
                                }}
                              />
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Typography variant="body2" color="text.secondary">
                                Daily: {(meter.average_annual_flow / 365).toFixed(0)} {unitLabels().volDay}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={3}>
                              <Typography variant="body2" color="text.secondary">
                                Monthly: {(meter.average_annual_flow / 12).toFixed(0)} {unitLabels().volMonth}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>
                      }
                    />
                  </ListItem>
                        ))}
                      </>
                    );
                  }

                  // No filters applied - show all
                  return filteredMeters.map((meter) => (
                    <ListItem key={meter.meter_product_id} divider>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1">
                              {meter.display_name || meter.meter_product_id}
                            </Typography>
                            <Chip label={meter.product_type} size="small" />
                            {modifiedFlowRates.has(meter.meter_product_id) && (
                              <Chip label="Modified" size="small" color="warning" />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Grid container spacing={2} alignItems="center">
                              <Grid item xs={12} md={4}>
                                <TextField
                                  label={`Annual Flow (${unitLabels().volYear})`}
                                  type="number"
                                  value={meter.average_annual_flow}
                                  onChange={(e) => updateMeterFlowRate(meter.meter_product_id, parseFloat(e.target.value) || 0)}
                                  size="small"
                                  fullWidth
                                  InputProps={{
                                    inputProps: { min: 0, step: 1000 }
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Typography variant="body2" color="text.secondary">
                                  Daily: {(meter.average_annual_flow / 365).toFixed(0)} {unitLabels().volDay}
                                </Typography>
                              </Grid>
                              <Grid item xs={12} md={3}>
                                <Typography variant="body2" color="text.secondary">
                                  Monthly: {(meter.average_annual_flow / 12).toFixed(0)} {unitLabels().volMonth}
                                </Typography>
                              </Grid>
                            </Grid>
                          </Box>
                        }
                      />
                    </ListItem>
                  ));
                })()}
                {meterFlowRates.length === 0 && (
                  <ListItem>
                    <Typography variant="body2" color="text.secondary">
                      No meter-product combinations found. Meters will appear here after proving reports are uploaded.
                    </Typography>
                  </ListItem>
                )}
              </List>

              {modifiedFlowRates.size > 0 && (
                <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                    onClick={saveMeterFlowRates}
                    disabled={loading || !canEditFlowRates}
                  >
                    Save Flow Rate Changes ({modifiedFlowRates.size} items)
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      loadMeterFlowRates(); // Reload original values
                    }}
                  >
                    Cancel Changes
                  </Button>
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Cal's reference documents — this site's own standards library. Admin-gated inside the
            component (edit_settings), like every other site-wide setting. */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Cal's Reference Documents</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <KnowledgeBaseSettings />
          </AccordionDetails>
        </Accordion>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {!canEditSettings ? (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
            View only — you don't have permission to edit these site-wide settings.
          </Typography>
        ) : <span />}
        <Box>
          <Button onClick={onClose}>Close</Button>
          <Button
            onClick={async () => {
              await savePreferences();
              if (modifiedFlowRates.size > 0) {
                await saveMeterFlowRates();
              }
            }}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={loading || !canEditSettings}
            sx={{ ml: 1 }}
          >
            Save All Settings
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UserPreferences;